import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import WorkOutlineIcon from '@material-ui/icons/WorkOutline';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { BASE_END_POINT_COMPANY } from '../../../../config';
import { buildJobUrl } from '../../../../helpers/urlHelpers';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(6, 2),
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4)
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary
  },
  subtitle: {
    color: theme.palette.text.secondary,
    maxWidth: 600,
    margin: '0 auto'
  },
  jobCard: {
    height: '100%',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
      borderColor: theme.palette.primary.main
    }
  },
  cardContent: {
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  companyLogo: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    objectFit: 'contain',
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`
  },
  jobTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4,
    minHeight: '2.8em'
  },
  companyName: {
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  detailsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  icon: {
    fontSize: '1rem'
  },
  categoryChip: {
    marginTop: 'auto',
    alignSelf: 'flex-start'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4)
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  }
}));

const SimilarJobs = ({ currentJobId, category, maxJobs = 6 }) => {
  const classes = useStyles();
  const history = useHistory();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  console.log('🎯 [SimilarJobs] Component mounted with:', { currentJobId, category, maxJobs });

  useEffect(() => {
    const fetchSimilarJobs = async () => {
      console.log('🔄 [SimilarJobs] Starting fetch with category:', category);
      
      if (!category) {
        console.warn('⚠️ [SimilarJobs] No category provided, skipping fetch');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(`🔍 [SimilarJobs] Fetching similar jobs for category: ${category}`);

        const params = new URLSearchParams();
        params.append('category', category);
        params.append('page', 0);
        params.append('size', maxJobs + 5); // Fetch extra to account for filtering current job

        const url = `${BASE_END_POINT_COMPANY}/api/v1/job/active?${params.toString()}`;
        console.log(`🌐 [SimilarJobs] Fetching from: ${url}`);

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        console.log(`✅ [SimilarJobs] Received response:`, result);
        console.log(`📊 [SimilarJobs] Response structure - data:`, result.data);
        console.log(`📊 [SimilarJobs] Data is array:`, Array.isArray(result.data));

        if (result.data && Array.isArray(result.data)) {
          // Filter out current job and limit to maxJobs
          const filteredJobs = result.data
            .filter(jobResult => jobResult.job?.id !== currentJobId)
            .slice(0, maxJobs);

          console.log(`📋 [SimilarJobs] Filtered to ${filteredJobs.length} jobs (excluded current job)`);
          setJobs(filteredJobs);
        } else {
          console.warn('⚠️ [SimilarJobs] Unexpected response format:', result);
          console.warn('📊 [SimilarJobs] Expected result.data to be an array but got:', {
            hasData: !!result.data,
            dataType: typeof result.data,
            isArray: Array.isArray(result.data)
          });
          setJobs([]);
        }
      } catch (error) {
        console.error('❌ [SimilarJobs] Failed to fetch:', error);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSimilarJobs();
  }, [currentJobId, category, maxJobs]);

  const handleJobClick = (jobId, jobTitle, companyName) => {
    console.log(`🔗 [SimilarJobs] Navigating to job: ${jobId}`);
    history.push(buildJobUrl(jobId, jobTitle, companyName));
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatJobType = (type) => {
    if (!type) return 'N/A';
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      dev: 'Development',
      design: 'Design',
      marketing: 'Marketing',
      sales: 'Sales',
      customer_support: 'Customer Support',
      finance: 'Finance',
      operations: 'Operations',
      hr: 'Human Resources',
      legal: 'Legal',
      other: 'Other'
    };
    return labels[cat] || cat;
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Typography variant="h4" className={classes.title}>
            Similar Jobs You Might Like
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Loading similar jobs in category: {category || 'N/A'}...
          </Typography>
        </Box>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (jobs.length === 0) {
    console.log('⚠️ [SimilarJobs] No jobs to display, hiding section');
    return null; // Don't show section if no similar jobs
  }

  console.log('✅ [SimilarJobs] Rendering', jobs.length, 'similar jobs');

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          Similar Jobs You Might Like
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Explore more opportunities in {getCategoryLabel(category)}
        </Typography>
      </Box>

      <Grid container spacing={3} style={{ maxWidth: 1200, margin: '0 auto' }}>
        {jobs.map((jobResult) => {
          const job = jobResult.job;
          const company = jobResult.company || {};
          const file = jobResult.file || {};

          return (
            <Grid item xs={12} sm={6} md={4} key={job.id}>
              <Card className={classes.jobCard}>
                <CardActionArea
                  onClick={() => handleJobClick(job.id, job.title, company.name)}
                  style={{ height: '100%' }}>
                  <CardContent className={classes.cardContent}>
                    {file.publicUrl && (
                      <img
                        src={file.publicUrl}
                        alt={company.name || 'Company logo'}
                        className={classes.companyLogo}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}

                    <Typography className={classes.jobTitle}>
                      {job.title}
                    </Typography>

                    <Typography className={classes.companyName}>
                      {company.name || 'Company'}
                    </Typography>

                    <Box className={classes.detailsRow}>
                      <WorkOutlineIcon className={classes.icon} />
                      <Typography variant="body2">
                        {formatJobType(job.type)}
                      </Typography>
                    </Box>

                    <Box className={classes.detailsRow}>
                      <LocationOnIcon className={classes.icon} />
                      <Typography variant="body2">
                        {job.regionalRestrictions || '🌍 Worldwide'}
                      </Typography>
                    </Box>

                    <Chip
                      label={getCategoryLabel(job.category)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className={classes.categoryChip}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

SimilarJobs.propTypes = {
  currentJobId: PropTypes.string.isRequired,
  category: PropTypes.string.isRequired,
  maxJobs: PropTypes.number
};

export default SimilarJobs;

